import { memo } from 'react';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import {
  faDivide,
  faGreaterThanEqual,
  faInfinity,
  faLessThanEqual,
  faMinus,
  faNotEqual,
  faPi,
  faPlus,
  faSquareRootAlt,
  faSubscript,
  faSuperscript,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette }) => ({
  // don't need appbar when printing (ie. course-instructors/:id/contract)
  '@media print': { display: 'none' },
  '.EFB-iconButton': {
    color: palette.text.secondary,
    height: 24,
    width: 24,
    '&:hover': {
      color: palette.text.primary,
      backgroundColor: 'transparent',
    },
  },
  '.EFB-symbol': {
    fontSize: '0.6em',
    letterSpacing: '-0.05em',
  },
});

export default memo(function EquationFieldButtons({ className, extraFieldButtonsString, onMQCommand, onMQWrite }) {
  const buttons = [
    ...DEFAULT_BUTTONS,
    ...(extraFieldButtonsString ?
      EXTRA_BUTTONS_CONFIG.filter(({ commands }) => commands.some(c => extraFieldButtonsString.includes(c))).flatMap(
        ({ buttons }) => buttons,
      )
    : []),
  ].map(({ mqCommand, mqWrite, ...rest }) => ({
    ...rest,
    handler() {
      mqWrite && onMQWrite(mqWrite);
      mqCommand && onMQCommand(mqCommand);
    },
  }));

  function renderButton(key, { handler, icon, label, tooltip }) {
    const btn = (
      <IconButton
        key={key}
        className="EFB-iconButton"
        onMouseDown={e => {
          e.preventDefault();
          handler();
        }}
        size="small"
        tabIndex={-1}
      >
        {icon ?
          <FontAwesomeIcon fixedWidth icon={icon} size="xs" />
        : label ?
          <Typography className="EFB-symbol" variant="caption">
            {label}
          </Typography>
        : null}
      </IconButton>
    );
    if (tooltip) {
      return (
        <Tooltip key={key} title={tooltip}>
          {btn}
        </Tooltip>
      );
    } else return btn;
  }

  return (
    <div className={className} css={rootCss}>
      {buttons.map((config, idx) => renderButton(idx, config))}
    </div>
  );
});

const DEFAULT_BUTTONS = [
  {
    icon: faPlus,
    mqWrite: '+',
  },
  {
    icon: faMinus,
    mqWrite: '-',
  },
  {
    icon: faTimes,
    mqWrite: '\\cdot',
  },
  {
    icon: faDivide,
    mqCommand: '\\frac',
  },
  {
    icon: faSquareRootAlt,
    mqCommand: '\\sqrt',
  },

  {
    icon: faInfinity,
    mqWrite: '\\infty',
  },
];
const EXTRA_BUTTONS_CONFIG = [
  {
    commands: ['^', '_'],
    buttons: [
      {
        icon: faSuperscript,
        mqCommand: '^',
      },
      {
        icon: faSubscript,
        mqCommand: '_',
      },
    ],
  },
  {
    commands: ['\\pi'],
    buttons: [
      {
        icon: faPi,
        mqWrite: '\\pi',
      },
    ],
  },
  {
    commands: ['\\le', '\\ge', '\\ne'],
    buttons: [
      {
        icon: faLessThanEqual,
        mqWrite: '\\le',
      },
      {
        icon: faGreaterThanEqual,
        mqWrite: '\\le',
      },
      {
        icon: faNotEqual,
        mqWrite: '\\ne',
      },
    ],
  },
  {
    commands: ['DNE'],
    buttons: [
      {
        label: 'DNE',
        textSmall: true,
        mqWrite: '\\text{DNE}',
        tooltip: 'Does Not Exist (∄)',
      },
    ],
  },
];
